import { buildPermission } from "common/utils/auth";
import {
  PermissionAction,
  PermissionDomain,
  PermissionResource,
} from "protos/portal/auth";
import { useSelf } from "portal/state/store";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import React, {
  ComponentType,
  FunctionComponent,
  PropsWithChildren,
  ReactNode,
} from "react";

type Permission = string;
type PermissionSet = Record<Permission, true>;

// component version of isAuthorized
interface WithAuthorizationRequiredAbstractProps extends PropsWithChildren {
  authChecker: (permissions: PermissionSet) => boolean;
}

const InternalWithAuthorizationRequiredAbstract: FunctionComponent<
  WithAuthorizationRequiredAbstractProps
> = ({ children, authChecker }) => {
  const { permissions } = useSelf();

  if (!authChecker(permissions)) {
    return;
  }

  return children;
};

// HOC version of isAuthorized
const internalWithAuthorizationRequiredAbstract = <P extends object>(
  authChecker: (permissions: PermissionSet) => boolean,
  Component: ComponentType<P>,
  otherwise?: ReactNode
): FunctionComponent<P> =>
  function InternalWithAuthorizationRequired(props: P): ReactNode {
    const { permissions } = useSelf();

    if (!authChecker(permissions)) {
      return otherwise;
    }

    return <Component {...props} />;
  };

////////////////////////////////////////////////////////////////////////////////
// legacy authorization primitives.  these work fine but they are limited in
// that they can only express permissions in "OR of ANDs" form; you can request
// multiple permissions, but they must be simple single permissions.
////////////////////////////////////////////////////////////////////////////////

/**
 * @returns true if the user has the given permissions
 **/
export const useAuthorizationRequired = (
  permissionStatement: PermissionStatement
): boolean => {
  const { permissions } = useSelf();
  return isAuthorized(permissions, permissionStatement);
};

interface WithAuthorizationRequiredLegacyProps extends PropsWithChildren {
  permissionStatement: PermissionStatement;
}

const InternalWithAuthorizationRequiredLegacy: FunctionComponent<
  WithAuthorizationRequiredLegacyProps
> = ({ children, permissionStatement }) => {
  return (
    <InternalWithAuthorizationRequiredAbstract
      authChecker={(permissions: PermissionSet) =>
        isAuthorized(permissions, permissionStatement)
      }
    >
      {children}
    </InternalWithAuthorizationRequiredAbstract>
  );
};

export const WithAuthorizationRequired: FunctionComponent<WithAuthorizationRequiredLegacyProps> =
  withAuthenticationRequired(InternalWithAuthorizationRequiredLegacy);

export const withAuthorizationRequired = <P extends object>(
  permissionStatement: PermissionStatement,
  Component: ComponentType<P>,
  otherwise?: ReactNode
): FunctionComponent<P> =>
  withAuthenticationRequired(
    internalWithAuthorizationRequiredAbstract(
      (permissions: PermissionSet) =>
        isAuthorized(permissions, permissionStatement),
      Component,
      otherwise
    )
  );

////////////////////////////////////////////////////////////////////////////////
// new authorization primitives.  these deliver two main improvements over the
// legacy primitives:
//
// - using anyContainingPermission, a request for a permission in a given
//   domain also accepts any domain which contains it; "self" and "robot"
//   accept "customer" or "all" as well, etc.
// - the primitives now accept a "permission statement" which can accept an
//   arbitrary tree of boolean clauses.  for most practical purposes we probably
//   only need an "AND of ORs" form, but the additional complexity required to
//   accept an arbitrary tree is minimal.
////////////////////////////////////////////////////////////////////////////////
type PermissionStatement =
  | Permission
  | { and: PermissionStatement[] }
  | { or: PermissionStatement[] };

// the containment hierarchies for permission domains.  domainSupersets[D] =
// the list of domains which are a superset of D (non-strict; domainSupersets[D]
// always contains D itself.)
const domainSupersets: {
  [domain in PermissionDomain]: PermissionDomain[];
} = {
  [PermissionDomain.unknown_domain]: [PermissionDomain.unknown_domain],
  [PermissionDomain.none]: [PermissionDomain.none],
  [PermissionDomain.self]: [
    PermissionDomain.self,
    PermissionDomain.customer,
    PermissionDomain.all,
  ],
  [PermissionDomain.robot]: [
    PermissionDomain.robot,
    PermissionDomain.customer,
    PermissionDomain.all,
  ],
  [PermissionDomain.customer]: [
    PermissionDomain.customer,
    PermissionDomain.all,
  ],
  [PermissionDomain.all]: [PermissionDomain.all],
  [PermissionDomain.templates]: [PermissionDomain.templates],
  [PermissionDomain.UNRECOGNIZED]: [PermissionDomain.UNRECOGNIZED],
};

export function anyContainingPermission(
  action: PermissionAction,
  resource: PermissionResource,
  domain: PermissionDomain
): PermissionStatement {
  return {
    or: domainSupersets[domain].map((superset) =>
      buildPermission(action, resource, superset)
    ),
  };
}

function isAuthorized(
  permissions: PermissionSet,
  statement: PermissionStatement
): boolean {
  if (typeof statement === "string") {
    return Object.hasOwn(permissions, statement);
  } else if ("and" in statement) {
    return statement.and.every((clause) => isAuthorized(permissions, clause));
  } else if ("or" in statement) {
    return statement.or.some((clause) => isAuthorized(permissions, clause));
  }

  // should never get here, but fail safe
  return false;
}

/**
 * don't import this unless you're test code.
 */
export const __exportedForTesting = {
  isAuthorized,
};
